import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";

export type ProgressTone = "accent" | "success" | "warning" | "danger";

export interface ProgressProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
  /** Avancement courant. Omettez-le pour une progression indéterminée. */
  value?: number;
  max?: number;
  label?: ReactNode;
  /** Affiche le pourcentage — à droite du libellé en linéaire, au centre en anneau. */
  showValue?: boolean;
  /** `linear` (défaut) pour une largeur pleine, `ring` pour une zone compacte. */
  variant?: "linear" | "ring";
  size?: "sm" | "md" | "lg";
  tone?: ProgressTone;
}

const FILL: Record<ProgressTone, string> = {
  accent: "bg-accent",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
};

const STROKE: Record<ProgressTone, string> = {
  accent: "stroke-accent",
  success: "stroke-success",
  warning: "stroke-warning",
  danger: "stroke-danger",
};

const RING_SIZE = { sm: 36, md: 48, lg: 72 } as const;
const RING_STROKE = { sm: 4, md: 5, lg: 6 } as const;

/**
 * Barre ou anneau de progression.
 *
 * L'anneau n'est pas un caprice esthétique : dans une carte, une cellule de
 * tableau ou à côté d'un avatar, une barre pleine largeur ne tient pas et la
 * valeur se lit mal. L'anneau porte la même information dans un carré compact,
 * avec le pourcentage en son centre.
 *
 * Sans `value`, les deux variantes passent en mode indéterminé.
 */
export function Progress({
  className,
  value,
  max = 100,
  label,
  showValue,
  variant = "linear",
  size = "md",
  tone = "accent",
  ...props
}: ProgressProps) {
  const indeterminate = value === undefined;

  // On borne : une valeur hors intervalle produirait une barre qui déborde
  // ou un `aria-valuenow` invalide.
  const clamped = indeterminate ? 0 : Math.min(Math.max(value, 0), max);
  const percent = max === 0 ? 0 : Math.round((clamped / max) * 100);

  // `aria-valuenow` absent = progression indéterminée : c'est la convention
  // ARIA, et les lecteurs d'écran annoncent alors « occupé » plutôt que « 0 % ».
  const aria = {
    role: "progressbar" as const,
    "aria-valuenow": indeterminate ? undefined : clamped,
    "aria-valuemin": 0,
    "aria-valuemax": max,
    "aria-label": typeof label === "string" ? label : undefined,
  };

  if (variant === "ring") {
    const box = RING_SIZE[size];
    const stroke = RING_STROKE[size];
    const radius = (box - stroke) / 2;
    const circumference = 2 * Math.PI * radius;

    return (
      <div className={cn("inline-flex flex-col items-center gap-2", className)} {...props}>
        <div className="relative" style={{ width: box, height: box }}>
          <svg
            {...aria}
            width={box}
            height={box}
            // Rotation d'un quart de tour : sans elle, un anneau SVG démarre à
            // 3 heures alors qu'on l'attend à midi.
            className={cn(
              "-rotate-90 transform-gpu",
              indeterminate && "animate-spin will-change-transform",
            )}
            data-motion-essential={indeterminate ? "" : undefined}
          >
            <circle
              cx={box / 2}
              cy={box / 2}
              r={radius}
              fill="none"
              strokeWidth={stroke}
              className="stroke-surface-sunken"
            />
            <circle
              cx={box / 2}
              cy={box / 2}
              r={radius}
              fill="none"
              strokeWidth={stroke}
              strokeLinecap="round"
              // Le tracé complet est masqué par `dashoffset`, qu'on réduit à
              // mesure que la valeur monte : l'anneau se remplit.
              strokeDasharray={circumference}
              strokeDashoffset={
                indeterminate ? circumference * 0.75 : circumference * (1 - percent / 100)
              }
              className={cn(
                STROKE[tone],
                !indeterminate && "transition-[stroke-dashoffset] duration-slower ease-standard",
              )}
            />
          </svg>

          {showValue && !indeterminate && (
            <span className="absolute inset-0 grid place-items-center text-xs font-medium text-fg tabular-nums">
              {percent}%
            </span>
          )}
        </div>

        {label && <span className="text-xs text-fg-muted">{label}</span>}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-1.5", className)} {...props}>
      {(label || showValue) && (
        <div className="flex items-baseline justify-between gap-3 text-sm">
          {label && <span className="text-fg">{label}</span>}
          {showValue && !indeterminate && (
            <span className="font-mono text-xs text-fg-muted tabular-nums">{percent}%</span>
          )}
        </div>
      )}

      <div
        {...aria}
        className={cn(
          "w-full overflow-hidden rounded-full bg-surface-sunken inset-shadow-control",
          size === "sm" ? "h-1" : size === "lg" ? "h-3" : "h-2",
        )}
      >
        {indeterminate ? (
          <div
            data-motion-essential=""
            className={cn(
              "h-full w-full origin-left transform-gpu will-change-transform animate-indeterminate rounded-full",
              FILL[tone],
            )}
          />
        ) : (
          /*
            La barre occupe TOUJOURS la largeur pleine ; c'est un rognage qui en
            découvre la fraction utile. Animer `width` ferait recalculer la mise
            en page à chaque image, quarante fois par seconde et sur le fil
            principal — pour un rectangle dont rien ne dépend.

            `clip-path` plutôt que `scaleX` : une mise à l'échelle horizontale
            écraserait aussi les arrondis, et le bout de la barre paraîtrait
            taillé au couteau en dessous de 30 %. Le `round` de la forme `inset()`
            garde des extrémités parfaitement circulaires à n'importe quelle
            valeur, sans jamais toucher à la géométrie du document.
          */
          <div
            className={cn(
              "h-full w-full rounded-full will-change-[clip-path] transition-[clip-path] duration-slower ease-standard",
              FILL[tone],
            )}
            style={{ clipPath: `inset(0 ${100 - percent}% 0 0 round 9999px)` }}
          />
        )}
      </div>
    </div>
  );
}
